import {type ReactNode} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {getAvailableSubjects} from '#/lib/curriculum'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {CheckThick_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Text} from '#/components/Typography'

/**
 * The way this platform asks *who a thing is for*, in the four steps both
 * publishers ask it in.
 *
 * The question is the platform's own (`#/lib/curriculum`: an audience, a grade
 * and a subject), and so is the shape of asking it — three answers, each one
 * deciding what the next list can even contain, then the press that publishes.
 * A capsule and a document are two publishers of the same shelf: an author who
 * has published one of them has already learned how to publish the other, and
 * that is only true while there is one set of steps rather than two copies of
 * them. The copies started here, in the capsule's publisher, and moved out the
 * day the two were put side by side and found to have drifted: the document's
 * asked the same three questions as a flat form under a title, with the publish
 * press sitting under it disabled until the last answer landed, and the same
 * author who published a capsule read that as a different thing entirely.
 *
 * What a step *is*, in the four pieces below:
 *
 * - a step numbered `1..4`, whose circle is filled while it is the one being
 *   answered, ticked once it is behind the author, and grey until it is reached
 *   (`StepTitle` -- the number *is* the progress bar, so there is nothing else
 *   to read);
 * - the answers, as full-width cards in the order they are asked
 *   (`OptionCard`), with the subject's the one exception because a subject list
 *   is long and a row of small presses is what fits it (`SubjectOptions`);
 * - an answered step collapsed to what was answered, with a press to take it
 *   back (`SelectedTag`) -- which is also where each answer's *scope* is
 *   expressed, because clearing the grade has to clear the subject that the
 *   grade made possible and clearing the audience has to clear both.
 *
 * The last step is not a question at all -- it is the press, and it exists only
 * once there is something to publish with.
 */

/** One step: its title, and whatever that step is currently showing. */
export function Step({children}: {children: ReactNode}) {
  return <View style={[a.gap_md, a.w_full]}>{children}</View>
}

/**
 * A step's number, its title, and the state of every step at once.
 *
 * `activeIndex1` is the step the author is on, 1-based, and it is what makes the
 * circles a progress bar: the one before it is ticked, the one on it is filled,
 * the ones after it are grey. `pr_3xl` keeps a long title off the close button.
 */
export function StepTitle({
  index,
  title,
  activeIndex1,
}: {
  index: number
  title: string
  activeIndex1: number
}) {
  const t = useTheme()
  const active = activeIndex1 === index
  const completed = activeIndex1 > index
  return (
    <View style={[a.flex_row, a.gap_sm, a.pr_3xl]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.rounded_full,
          a.border,
          {
            width: 24,
            height: 24,
            backgroundColor: active
              ? t.palette.primary_500
              : completed
                ? t.palette.primary_100
                : t.atoms.bg_contrast_25.backgroundColor,
            borderColor: active
              ? t.palette.primary_500
              : completed
                ? t.palette.primary_400
                : t.atoms.border_contrast_low.borderColor,
          },
        ]}>
        {completed ? (
          <Check width={12} fill={t.palette.primary_500} />
        ) : (
          <Text
            style={[
              a.font_bold,
              a.text_center,
              {
                color: active
                  ? 'white'
                  : completed
                    ? t.palette.primary_700
                    : t.atoms.text_contrast_medium.color,
                fontVariant: ['tabular-nums'],
                fontSize: 12,
                lineHeight: 24,
              },
            ]}>
            {index}
          </Text>
        )}
      </View>

      <Text
        style={[
          a.flex_1,
          a.font_bold,
          a.text_lg,
          a.leading_snug,
          active ? t.atoms.text : t.atoms.text_contrast_medium,
          {
            top: 1,
          },
        ]}>
        {title}
      </Text>
    </View>
  )
}

/** An answer that can be given: one full-width card, pressable as a whole. */
export function OptionCard({
  label,
  onSelect,
  testID,
}: {
  label: string
  onSelect?: () => void
  testID?: string
}) {
  const t = useTheme()
  return (
    <Button
      testID={testID}
      label={label}
      onPress={onSelect}
      disabled={!onSelect}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.px_md,
            a.py_sm,
            a.rounded_sm,
            a.border,
            t.atoms.bg_contrast_25,
            hovered || pressed
              ? [t.atoms.border_contrast_high]
              : [t.atoms.border_contrast_low],
          ]}>
          <Text style={[a.text_md, a.font_bold, a.leading_snug]}>{label}</Text>
        </View>
      )}
    </Button>
  )
}

/**
 * An answer that has been given: the same card, inert, with the press that
 * takes it back beside it.
 *
 * Deliberately the same card as the one that offered it -- the author is reading
 * their own answer back, not a summary of it written in another voice.
 */
export function SelectedTag({
  label,
  onClear,
}: {
  label: string
  onClear: () => void
}) {
  const {t: l} = useLingui()
  return (
    <View style={[a.flex_row, a.align_center, a.gap_md]}>
      <View style={[a.flex_1]}>
        <OptionCard label={label} />
      </View>
      <Button
        label={l(msg`Clear selection`)}
        size="tiny"
        variant="solid"
        color="secondary"
        shape="round"
        onPress={onClear}>
        <ButtonIcon icon={X} />
      </Button>
    </View>
  )
}

/**
 * The subjects a grade can be asked about, as the one list long enough to want
 * small presses instead of cards.
 *
 * The list is the grade's own (`getAvailableSubjects`) rather than every subject
 * the platform knows: a subject list that ignores the grade is a list of wrong
 * answers.
 */
export function SubjectOptions({
  grade,
  onSelect,
  testID,
}: {
  /** The grade that decides the list -- `null` is a grade nobody chose yet. */
  grade: string | null
  onSelect: (value: string) => void
  /** Names each press for a harness, which is how a suite picks a subject. */
  testID?: (value: string) => string
}) {
  const {t: l} = useLingui()
  return (
    <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
      {getAvailableSubjects(grade).map(s => (
        <Button
          key={s.value}
          testID={testID?.(s.value)}
          variant="outline"
          color="secondary"
          size="small"
          label={l(s.label)}
          onPress={() => onSelect(s.value)}>
          <ButtonText>{l(s.label)}</ButtonText>
        </Button>
      ))}
    </View>
  )
}
